import {
  BehaviorSubject,
  filter,
  of,
  Subject,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { getDefaultGroups } from '@shared/utils/control';
import { AlertService } from '@shared/services/alert.service';
import _ from 'lodash';
import { FormGroup } from '@angular/forms';
import { ControlErrorAreaDirective } from '@shared/directives/control-error-area.directive';
import { inject, Injectable } from '@angular/core';
import { VALIDATION_PROVIDER } from '@shared/providers/validate-cargo.provider';
import {
  ControlError,
  ControlGroupButton,
  ControlRecord,
  ControlRecordGroups,
  ControlRecordMessageType,
} from '../../types/control';

@Injectable()
export class ControlService {
  private readonly alert = inject(AlertService);
  private readonly control = inject(VALIDATION_PROVIDER);

  error$ = new Subject<ControlError>();
  records$ = new BehaviorSubject<ControlRecordGroups>(getDefaultGroups());
  selectedGroup$ = new BehaviorSubject<ControlRecordMessageType>('error');

  collapsed$ = new BehaviorSubject<boolean>(true);
  readonly buttons: ControlGroupButton[] = [
    {
      name: 'error',
      title: 'Ошибки',
      icon: 'tuiIconCircleX',
      iconColor: 'red',
    },
    {
      name: 'warning',
      title: 'Предупреждения',
      icon: 'tuiIconTriangleAlert',
      iconColor: 'orange',
    },
    {
      name: 'info',
      title: 'Информация',
      icon: 'tuiIconInfo',
      iconColor: 'black',
    },
  ];

  isWareDetailsError(record: ControlRecord) {
    return this.isErrorContainsSubstring(record, 'ware_details');
  }

  isPrecedingDocumentsError(record: ControlRecord) {
    return this.isErrorContainsSubstring(record, 'preceding_documents_json');
  }

  private isErrorContainsSubstring(record: ControlRecord, substring: string) {
    const [pathAndIndex] = record.items ?? [];
    const [path] = Object.keys(pathAndIndex);
    return path.includes(substring);
  }

  isWareDetailsMeasureError(record: ControlRecord) {
    return this.isErrorContainsSubstring(record, 'details_measure_unit_digit');
  }

  getWareIndexFromWareDetailsError(record: ControlRecord): number {
    const [pathAndIndex] = record.items ?? [];
    const [path] = Object.keys(pathAndIndex);
    const [, index] = path.split('.');
    return Number(index) - 1;
  }

  private setRecordGroups(records: ControlRecord[]) {
    const groups = getDefaultGroups();
    records.forEach(record => {
      const type = record.message.type;
      groups[type].push(record);
    });
    this.records$.next(groups);
  }

  private selectFirstGroupWithErrors() {
    const groups = this.records$.value;
    const keys = Object.keys(groups);
    const group = Object.values(groups);
    const index = group.findIndex(errors => errors.length);
    const selectedKey = keys[index || 0];
    this.selectedGroup$.next(selectedKey as ControlRecordMessageType);
  }

  validate(id: number) {
    return of(id).pipe(
      take(1),
      switchMap(value => this.control.validate(value)),
      tap(records => this.setRecordGroups(records)),
      tap(() => this.selectFirstGroupWithErrors()),
      tap(records => {
        const errorsAndWarnings = records.filter(
          record => record.message.type !== 'info',
        );
        if (!errorsAndWarnings.length) {
          this.alert.showSuccess('Ошибки отсутствуют');
        }
      }),
      tap(() => {
        if (this.collapsed$.value) {
          this.collapsed$.next(false);
        }
      }),
    );
  }

  getSiblingField(key: string) {
    const fields: Record<string, string> = {
      countryName: 'countryLetter',
      subjectPersonName: 'subjectName',
      counterAgentPersonName: 'counterAgentName',
      declarantPersonName: 'declarantName',
      dispatchCountryName: 'dispatchCountryLetter',
      destinationCountryName: 'destinationCountryLetter',
      originCountryName: 'originCountryLetter',
      tradeCountryName: 'tradeCountryLetter',
      title: 'description',
      addMeasureUnitLetter: 'addMeasureUnitDigit',
      specificMeasureUnitLetter: 'specificMeasureUnitDigit',
      trackedMeasureUnitLetter: 'trackedMeasureUnitDigit',
      controlledMeasureUnitLetter: 'controlledMeasureUnitDigit',
      identityDocCode: 'identityDocName',
      destinationCustomsCountryLetter: 'destinationCustomsCountryLetter',
    };

    return key in fields ? fields[key] : key;
  }

  observe<T>(block: keyof T, compareFn?: (record: ControlRecord) => boolean) {
    return this.error$.pipe(
      filter(({ record }) => {
        if (compareFn) {
          return compareFn(record);
        }

        if (record.items?.length) {
          const [element] = record.items;
          const [blockName] = Object.keys(element);
          const [blockSnakeCase] = blockName.split('.') ?? [];
          const controlBlock = _.camelCase(blockSnakeCase);
          console.log({ block, controlBlock });
          return controlBlock === block;
        }
        const [blockSnakeCase] = record?.path?.split('.') ?? [];
        const controlBlock = _.camelCase(blockSnakeCase);
        console.log({ block, controlBlock });
        return controlBlock === block;
      }),
    );
  }

  showField(record: ControlRecord, index: number | null = null) {
    this.error$.next({ record, index });
  }

  private getControlKeyName(error: ControlError) {
    if (error.record.items?.length) {
      const [element] = error.record.items ?? [];
      const [blockName] = Object.keys(element);
      const parts = blockName.split('.');
      const blockSnakeCase = parts[parts.length - 1];
      const controlBlock = _.camelCase(blockSnakeCase);
      return this.getSiblingField(controlBlock);
    }
    const parts = error.record?.path?.split('.') ?? [];
    const [, ...fieldsList] = error.record?.path?.split('.') ?? [];
    const fields = parts.length === 1 ? parts : fieldsList;
    const camelCaseFields = fields.map(field => _.camelCase(field));
    return this.getSiblingField(camelCaseFields[camelCaseFields.length - 1]);
  }

  private getControlFromItems(formGroup: FormGroup, error: ControlError) {
    const key = this.getControlKeyName(error);
    return formGroup.controls[key];
  }

  getControlFromPath(formGroup: FormGroup, error: ControlError) {
    const [, ...fields] = error.record?.path?.split('.') ?? [];
    const camelCaseFields = fields.map(field => _.camelCase(field));
    const [first, second] = camelCaseFields;
    const firstKey = this.getSiblingField(first);
    const secondKey = this.getSiblingField(second);
    const path =
      camelCaseFields.length <= 1
        ? `controls.${firstKey}`
        : `controls.${firstKey}.controls.${secondKey}`;

    return _.get(formGroup, path);
  }

  highlightArea(
    error: ControlError,
    controlErrorAreas: Map<string, ControlErrorAreaDirective>,
  ) {
    const name = this.getControlKeyName(error);
    const area = controlErrorAreas.get(name);
    area?.scrollIntoView();
    area?.highlight();
    area?.focus();
  }
}
